import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, AlertCircle, X } from "lucide-react";
import { useQrScanner } from "../hooks/useQrScanner";
import { QrCameraView } from "./QrCameraView";
import { Spinner } from "./ui";

type CheckInOverlayProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function CheckInOverlay({ isOpen, onClose }: CheckInOverlayProps) {
  const { phase, open, close, onScan, onPermissionDenied, confirm, retry } = useQrScanner();

  // Sync open/close with parent
  useEffect(() => {
    if (isOpen) open();
    else close();
  }, [isOpen, open, close]);

  // Auto-close after success
  useEffect(() => {
    if (phase.step !== "success") return;
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [phase.step, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const handleClose = () => {
    close();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          className="fixed inset-0 z-[60] bg-surface flex flex-col"
          style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h1 className="text-lg font-semibold text-foreground">Check In</h1>
            <button
              onClick={handleClose}
              className="p-2 -mr-2 text-muted hover:text-foreground transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {phase.step === "scanning" && (
              <QrCameraView onScan={onScan} onPermissionDenied={onPermissionDenied} />
            )}

            {phase.step === "loading" && (
              <div className="flex flex-col items-center justify-center h-full px-6">
                <Spinner label="Loading check-in details..." />
              </div>
            )}

            {phase.step === "confirm" && (
              <div className="flex flex-col items-center justify-center h-full px-6">
                <div className="w-full max-w-sm">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-[#2D5A4A]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-[#2D5A4A]" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-semibold text-foreground mb-1">
                      Confirm Kiosk Check-In
                    </h2>
                    <p className="text-sm text-muted">
                      Verify you want to check in at this kiosk
                    </p>
                  </div>

                  <div className="space-y-3 mb-8">
                    <InfoPill label="Center" value={phase.challenge.center_name} />
                    <InfoPill label="Device" value={phase.challenge.device_name} />
                    {phase.challenge.device_location && (
                      <InfoPill label="Location" value={phase.challenge.device_location} />
                    )}
                  </div>

                  <button
                    onClick={() => confirm(phase.token)}
                    className="w-full py-4 bg-[#2D5A4A] text-white text-lg font-medium rounded-xl hover:bg-[#24493c] active:scale-[0.98] transition-all"
                  >
                    Confirm Check-In
                  </button>

                  <button
                    onClick={handleClose}
                    className="w-full mt-3 py-3 text-sm text-muted hover:text-foreground transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {phase.step === "confirming" && (
              <div className="flex flex-col items-center justify-center h-full px-6">
                <Spinner label="Confirming..." />
              </div>
            )}

            {phase.step === "success" && (
              <div className="flex flex-col items-center justify-center h-full px-6">
                <div className="w-full max-w-sm text-center">
                  <div className="w-16 h-16 bg-success-bg rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-success-text" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground mb-1">
                    You're Checked In!
                  </h2>
                  <p className="text-sm text-muted mb-2">
                    {phase.device_name}
                    {phase.device_location ? ` \u2022 ${phase.device_location}` : ""}
                  </p>
                  <p className="text-xs text-muted mt-4">
                    Closing automatically...
                  </p>
                </div>
              </div>
            )}

            {phase.step === "error" && (
              <div className="flex flex-col items-center justify-center h-full px-6">
                <div className="w-full max-w-sm text-center">
                  <div className="w-16 h-16 bg-danger-bg rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-danger" />
                  </div>
                  <h2 className="text-lg font-semibold text-foreground mb-2">
                    Unable to Check In
                  </h2>
                  <p className="text-sm text-muted mb-6">
                    {phase.message}
                  </p>
                  <div className="flex flex-col gap-3">
                    {phase.canRetry && (
                      <button
                        onClick={retry}
                        className="w-full py-3 bg-[#2D5A4A] text-white font-medium rounded-xl hover:bg-[#24493c] active:scale-[0.98] transition-all"
                      >
                        Scan Again
                      </button>
                    )}
                    <button
                      onClick={handleClose}
                      className="w-full py-3 text-sm text-muted hover:text-foreground transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {phase.step === "permission-denied" && (
              <div className="flex flex-col items-center justify-center h-full px-6">
                <div className="w-full max-w-sm text-center">
                  <div className="w-16 h-16 bg-warning-bg rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-warning-text" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-foreground mb-2">
                    Camera Access Required
                  </h2>
                  <p className="text-sm text-muted mb-6">
                    To scan QR codes, please allow camera access in your browser settings and try again.
                  </p>
                  <button
                    onClick={handleClose}
                    className="w-full py-3 bg-[#2D5A4A] text-white font-medium rounded-xl hover:bg-[#24493c] active:scale-[0.98] transition-all"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function InfoPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-3 px-4 bg-surface2 rounded-xl">
      <span className="text-sm text-muted">{label}</span>
      <span className="text-sm font-medium text-foreground">{value}</span>
    </div>
  );
}
